import { useToast } from '@medely/ui-kit';
import React, { createContext, useState, ReactNode, useContext } from 'react';

interface GeoLocationCoords {
  latitude: number;
  longitude: number;
  accuracy: number;
  altitude?: number;
  altitudeAccuracy?: number;
  heading?: number;
  speed?: number;
}

interface GeoLocationData {
  coords: GeoLocationCoords;
  timestamp: number;
}

interface GeoLocationContextType {
  location: GeoLocationData | null;
  error: boolean | null;
  isLoading: boolean | null;
  isGeolocationAvailable: boolean | null;
  locationPermissionsGranted: boolean;
  requestUserRecentLocation: () => Promise<GeoLocationData | null>;
  requestLocationPermissions: () => Promise<void>;
}

export const GeoLocationContext = createContext<GeoLocationContextType | undefined>(undefined);

interface GeoLocationProviderProps {
  children: ReactNode;
}

export const GeoLocationProvider: React.FC<GeoLocationProviderProps> = ({ children }) => {
  const toast = useToast();
  const [location, setLocation] = useState<GeoLocationData | null>(null);
  const [error, _setError] = useState<boolean | null>(null); // TD: USE THIS SETTER OR REMOVE
  const [isLoading, setIsLoading] = useState<boolean | null>(null);
  const [isGeolocationAvailable, _setIsGeolocationAvailable] = useState<boolean | null>(null); // TD: USE THIS SETTER OR REMOVE
  const [locationPermissionsGranted, setLocationPermissionsGranted] = useState<boolean>(false);

  const requestLocationPermissions = async () => {
    setIsLoading(true);
    if (!navigator.geolocation) {
      console.error('Geolocation is not supported by this browser.');
      return;
    }

    try {
      const permissionStatus = await navigator.permissions.query({
        name: 'geolocation',
      });

      if (permissionStatus.state === 'prompt') {
        navigator.geolocation.getCurrentPosition(
          // TD: USE THIS POSITION!
          (_position) => {
            // Success: location access granted
            setLocationPermissionsGranted(true);
          },
          (error) => {
            // Error: location access denied or an error occurred
            console.error('Error getting location:', error);
            setLocationPermissionsGranted(false);
          },
        );
      } else if (permissionStatus.state === 'granted') {
        // Location access already granted
        setLocationPermissionsGranted(true);
        // navigator.geolocation.getCurrentPosition(
        //   (position) => {
        //     console.log('Location access already granted:', position.coords);
        //     setLocationPermissionsGranted(true);
        //   },
        //   (error) => {
        //     console.error('Error getting location:', error);
        //     setLocationPermissionsGranted(false);
        //   },
        // );
      } else {
        // Location access denied
        console.warn('Location access denied.');
        setLocationPermissionsGranted(false);
      }
    } catch (error) {
      console.error('Error checking location permission:', error);
      setLocationPermissionsGranted(false);
    }
    setIsLoading(false);
  };

  const requestUserRecentLocation = async () => {
    setIsLoading(true);
    if (navigator.geolocation) {
      try {
        const position = (await new Promise((resolve, reject) => {
          navigator.geolocation.getCurrentPosition(resolve, reject);
        })) as GeolocationPosition;
        const { accuracy, latitude, longitude } = position.coords;
        const locationData = {
          coords: {
            accuracy,
            latitude,
            longitude,
          },
        } as GeoLocationData;
        setLocation(locationData);
        return locationData;
        // handleContinueWithLocationPermissions();
      } catch (err) {
        toast.error('Unable to retrieve location');
        return null;
      } finally {
        setIsLoading(false);
      }
    } else {
      // Geolocation is not supported by this browser
      setIsLoading(false);
      return null;
    }
  };

  return (
    <GeoLocationContext.Provider
      value={{
        location,
        error,
        isLoading,
        requestUserRecentLocation,
        requestLocationPermissions,
        isGeolocationAvailable,
        locationPermissionsGranted,
      }}
    >
      {children}
    </GeoLocationContext.Provider>
  );
};

export const useGeoLocationContext = () => {
  const context = useContext(GeoLocationContext);

  if (context === undefined) {
    throw new Error('useGeoLocationContext must be used within a GeoLocationContext');
  }

  return context;
};
